import { enums, formatters, selectors } from "@fraction/shared";
import { formatDistanceToNow } from "date-fns";
import _ from "lodash";
import { useApplicationAuthed } from "src/hooks/useApplication";
import CalendarIllustration from "src/images/calendar-icon.svg?react";
import InvestmentPropertyIcon from "src/images/illustrative-buttons/investment-property-icon.svg?react";
import MoneyIcon from "src/images/illustrative-buttons/savings-and-investments-icon.svg?react";
import RateIllustration from "src/images/rate-percentage-icon.png";
import { cn } from "src/utilities/shadcnUtils";

export const NotableAppInformation = ({
  applicationId,
  className,
}: {
  applicationId?: string;
  className?: string;
}) => {
  const { data: app } = useApplicationAuthed({
    id: applicationId,
  });

  if (
    !app?.appraisalAppointmentDate &&
    !app?.inspectionAppointmentDate &&
    !app?.estimatedCloseDate &&
    !app?.type &&
    !app?.requestedAmount
  ) {
    return null;
  }

  const valuation = app ? selectors.application.selectBestValuation(app)?.value : undefined;

  return (
    <div className={cn("flex flex-row gap-2 text-sm flex-wrap", className)}>
      <DateBox date={app?.appraisalAppointmentDate} label="Appraisal Appointment Date" />
      <DateBox date={app?.inspectionAppointmentDate} label="Inspection Appointment Date" />
      <DateBox date={app?.estimatedCloseDate} label="Estimated Close Date" />
      <RateBox
        rateType={app?.rateType}
        type={app?.interestType}
        rate={app?.minimumRate}
        label="Annual Rate"
      />
      <PaymentsBox
        type={app?.type}
        frequency={app?.paymentFrequency}
        termLengthMonths={app?.termLengthMonths}
        label="Payment type"
      />
      <LoanAmountBox
        confirmedAmount={app?.confirmedLoanAmount}
        approvedAmount={app?.approvedAmount}
        requestedAmount={app?.requestedAmount}
        label="Loan Amount"
        valuation={valuation}
      />
    </div>
  );
};

const DateBox = ({ date, label }: { date?: Date | null; label: string }) => {
  if (!date) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-md p-4 max-w-[280px]">
      <div className="flex flex-row items-center gap-x-2">
        <CalendarIllustration width={50} height={50} />
        <div>
          <p className="text-md font-bold">{label}</p>
          <p className="text-sm font-medium">{formatters.date.formatDate(date)}</p>
          <p className="text-xs text-gray-600">{formatDistanceToNow(date)}</p>
        </div>
      </div>
    </div>
  );
};

const RateBox = ({
  rate,
  label,
  type,
  rateType,
}: {
  rate?: number | null | any;
  label: string;
  type?: enums.InterestType | null;
  rateType?: enums.RateType | null;
}) => {
  if (!rate) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-md p-4 max-w-[280px]">
      <div className="flex flex-row items-center gap-x-2">
        <img src={RateIllustration} width={50} height={50} />
        <div>
          <p className="text-md font-bold">{label}</p>
          <p className="text-sm font-medium">
            {formatters.number.getPercentageFromDecimal(rate)}{" "}
            {rateType ? _.startCase(rateType).toLowerCase() : ""}
          </p>
          {type ? (
            <p className="text-xs text-gray-600">Interest is {_.startCase(type).toLowerCase()}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const PaymentsBox = ({
  label,
  type,
  frequency,
  termLengthMonths,
}: {
  label: string;
  type?: enums.ProductType | null;
  frequency?: enums.Frequency | null;
  termLengthMonths?: number | null;
}) => {
  if (!type) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-md p-4 max-w-[280px]">
      <div className="flex flex-row items-center gap-x-2">
        <InvestmentPropertyIcon width={50} height={50} />
        <div>
          <p className="text-md font-bold">{label}</p>
          <p className="text-sm font-medium">{formatters.enums.PRODUCT_TYPE_MAP[type]}</p>
          {![
            enums.ProductType.FIXED_RATE,
            enums.ProductType.HOLDBACK_MORTGAGE,
            enums.ProductType.REVERSE_MORTGAGE,
            enums.ProductType.NO_PAYMENTS_MORTGAGE,
            enums.ProductType.APPRECIATION_MORTGAGE,
            enums.ProductType.APPRECIATION_HELOC,
          ].includes(type) && frequency ? (
            <p className="text-xs text-gray-600">Payments are {_.startCase(frequency).toLowerCase()}</p>
          ) : null}
          {termLengthMonths ? (
            <p className="text-xs text-gray-600">Term length is {termLengthMonths} months</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const LoanAmountBox = ({
  confirmedAmount,
  approvedAmount,
  requestedAmount,
  label,
  valuation,
}: {
  confirmedAmount?: number | null;
  approvedAmount?: number | null;
  requestedAmount?: number | null;
  label: string;
  valuation?: number | null;
}) => {
  const amount = confirmedAmount || approvedAmount || requestedAmount;
  const status = confirmedAmount
    ? ""
    : approvedAmount
    ? "Approved for "
    : requestedAmount
    ? "Requested "
    : null;

  if (!amount || !status) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-md p-4 max-w-[280px]">
      <div className="flex flex-row items-center gap-x-2">
        <MoneyIcon width={50} height={50} />
        <div>
          <p className="text-md font-bold">{label}</p>
          <p className="text-sm font-medium">
            {status}
            {formatters.number.getCurrencyFromNumber(amount)}
          </p>
          {valuation ? (
            <p className="text-xs text-gray-600">
              LTV is {formatters.number.getPercentageFromDecimal(amount / valuation)}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
